import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { servicesData } from '../../data/servicesData';
import { industriesData } from '../../data/industriesData';
import { useModal } from '../../contexts/ModalContext';
const serviceSeoData = {
  'custom-software': {
    metaTitle: "Custom Software Development Dharapuram | Byte Pairs",
    metaDescription: "Tailored billing software, CRMs, and ERPs for Dharapuram businesses. Pay once, own the code forever. Eliminate bloated subscription packages with custom solutions.",
    keywords: "custom software development Dharapuram, billing software, custom ERP, custom CRM, Dharapuram, Tamil Nadu",
    faq: [
      {
        q: "What are the benefits of custom software over SaaS?",
        a: "Custom software has a one-time build cost, giving you full code ownership. You avoid monthly licensing fees and get features built specifically for your workflow."
      },
      {
        q: "How long does custom software development take?",
        a: "A standard custom database or business software tool takes between 6 to 12 weeks from initial scoping to cloud deployment."
      }
    ]
  },
  'web-apps': {
    metaTitle: "Web Application Development Studio | Byte Pairs",
    metaDescription: "High-performance customer portals, real-time dashboards, and custom business tools built with React & Next.js. Fast loading, responsive layout.",
    keywords: "web application development, React developer, Next.js portal, admin dashboard, client database web app",
    faq: [
      {
        q: "What is a web application portal?",
        a: "A web application portal is a secure, interactive website (like a dashboard or client log-in area) that allows customers or staff to manage records, invoices, and operations."
      }
    ]
  },
  'mobile-apps': {
    metaTitle: "iOS & Android Mobile App Developers | Byte Pairs",
    metaDescription: "Cross-platform mobile app development using React Native and Flutter. Get your business on customers' home screens with native-speed, offline-capable apps.",
    keywords: "mobile app development, React Native agency, Flutter app development, iOS Android app developer",
    faq: [
      {
        q: "Why use React Native or Flutter instead of native apps?",
        a: "React Native and Flutter let us write a single codebase that targets both iOS and Android. This cuts development and maintenance costs in half while providing native performance."
      }
    ]
  },
  'ai-automation': {
    metaTitle: "AI Chatbots & Business Automation | Byte Pairs",
    metaDescription: "Automate leads routing, WhatsApp notifications, and customer inquiries with smart API integrations. Capture and follow up with leads 24/7.",
    keywords: "WhatsApp automation, business automation API, AI chatbots, customer support bot, lead routing automation",
    faq: [
      {
        q: "How does WhatsApp automation work for small businesses?",
        a: "By integrating the official WhatsApp Business API, we trigger automatic receipts, booking confirmations, or customer notifications based on actions in your software or CRM."
      }
    ]
  },
  'website-seo': {
    metaTitle: "Website Development & SEO Studio | Byte Pairs",
    metaDescription: "Stunning static marketing websites optimized for Google search and generative AI engines (AEO/GEO). Super fast load times, responsive mobile design.",
    keywords: "website development, search engine optimization Dharapuram, local SEO, Answer Engine Optimization, Generative Engine Optimization",
    faq: [
      {
        q: "What are AEO and GEO optimizations?",
        a: "Answer Engine Optimization (AEO) and Generative Engine Optimization (GEO) are practices to ensure your content is easily quoted and cited by AI engines like ChatGPT, Gemini, and Perplexity."
      }
    ]
  },
  'design-branding': {
    metaTitle: "UI/UX Design & Brand Identity | Byte Pairs",
    metaDescription: "Modern logo design, brand systems, and interactive Figma wireframes. We design premium, responsive user interfaces built to maximize conversions.",
    keywords: "UI UX design, Figma prototyping, logo design Dharapuram, brand guidelines, mobile first design",
    faq: [
      {
        q: "Do you design layouts before writing code?",
        a: "Yes, we build interactive Figma wireframes so you can click through your application's buttons and pages to approve the user experience before any coding begins."
      }
    ]
  },
  'support-maintenance': {
    metaTitle: "Website Support & Cloud Maintenance | Byte Pairs",
    metaDescription: "Direct developer support, secure cloud hosting configurations (AWS/Vercel), automated backup management, and 24/7 uptime monitoring.",
    keywords: "cloud hosting support, website maintenance, database backup cloud, security updates, uptime monitoring",
    faq: [
      {
        q: "What is included in your software maintenance plans?",
        a: "We handle server uptime checks, security library updates, regular database backups, and dedicate monthly developer hours for small text or style changes."
      }
    ]
  }
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { openConsultation } = useModal();
  const service = servicesData[serviceId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center px-6">
        <Icon name="AlertTriangle" size={48} className="text-warning mb-4" />
        <h1 className="text-2xl font-bold mb-2">Service Not Found</h1>
        <p className="text-text-secondary mb-6 text-center max-w-md">
          The service you are looking for might have been renamed or moved. Check out our homepage for current options.
        </p>
        <Button variant="default" onClick={() => navigate('/')}>
          Return Home
        </Button>
      </div>
    );
  }

  const seo = serviceSeoData[serviceId] || {
    metaTitle: `${service.title} | Byte Pairs`,
    metaDescription: `${service.tagline} ${service.overview.substring(0, 140)}...`,
    keywords: `bytepairs, byte pairs, ${service.title.toLowerCase()}, software solutions`,
    faq: []
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.overview,
    "provider": {
      "@type": "ProfessionalService",
      "name": "Byte Pairs",
      "telephone": "+916374241548",
      "email": "bytepairs2026@gmail.com",
      "priceRange": "$$",
      "url": "https://bytepairs.com/",
      "image": "https://bytepairs.com/android-chrome-512x512.png",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Dharapuram",
        "addressLocality": "Dharapuram",
        "addressRegion": "Tamil Nadu",
        "postalCode": "638656",
        "addressCountry": "IN"
      }
    }
  };

  const faqSchema = seo.faq && seo.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": seo.faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://bytepairs.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://bytepairs.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": service.title,
        "item": `https://bytepairs.com/services/${service.id}`
      }
    ]
  };

  // Find detailed info for suitable industries
  const matchedIndustries = service.suitableIndustries
    .map(indId => industriesData[indId])
    .filter(Boolean);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <>
      <Helmet>
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href={`https://bytepairs.com/services/${service.id}`} />
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://bytepairs.com/services/${service.id}`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.metaTitle} />
        <meta name="twitter:description" content={seo.metaDescription} />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative py-20 bg-gradient-to-br from-primary/5 via-muted/30 to-accent/5 overflow-hidden border-b border-border">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(234,179,8,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(234,179,8,0.1)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            </div>
            <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Icon name={service.icon} size={16} />
                  <span>Our Service</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-4xl md:text-6xl font-black mb-6 tracking-tight"
              >
                {service.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-10"
              >
                {service.tagline}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand font-bold"
                  onClick={openConsultation}
                >
                  Book Free Consultation
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="border-primary/20 text-primary hover:bg-primary/5"
                  onClick={() => {
                    const el = document.getElementById('problems-section');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Learn More
                </Button>
              </motion.div>
            </div>
          </section>

          {/* Overview & Context */}
          <section className="py-20 bg-background">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl font-bold mb-6">Overview</h2>
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed">
                {service.overview}
              </p>
            </div>
          </section>

          {/* Problems Faced Section */}
          <section id="problems-section" className="py-20 bg-muted/20 border-t border-b border-border">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <div className="inline-flex items-center space-x-2 bg-destructive/10 text-destructive px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                  <Icon name="AlertCircle" size={14} />
                  <span>The Pain Points</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Challenges Businesses Face</h2>
                <p className="text-text-secondary mt-4 max-w-2xl mx-auto">
                  Outdated setups and fragmented tools create invisible friction that slows down daily growth.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {service.problems.map((prob, idx) => (
                  <motion.div
                    key={idx}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px' }}
                    variants={itemVariants}
                    className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg transition-all duration-300 flex items-start space-x-4"
                  >
                    <div className="w-10 h-10 bg-destructive/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name="XCircle" className="text-destructive" size={20} />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-2">Issue #{idx + 1}</h4>
                      <p className="text-text-secondary leading-relaxed">{prob}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Solutions Section */}
          <section className="py-20 bg-background">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                  <Icon name="CheckCircle" size={14} />
                  <span>Our Remedy</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">How We Solve Them</h2>
                <p className="text-text-secondary mt-4 max-w-2xl mx-auto">
                  We build lightweight, direct technical systems specifically matching your parameters.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {service.solutions.map((sol, idx) => (
                  <motion.div
                    key={idx}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px' }}
                    variants={itemVariants}
                    className="bg-card border border-border rounded-2xl p-8 hover:border-success/40 hover:shadow-brand transition-all duration-300 flex items-start space-x-4"
                  >
                    <div className="w-10 h-10 bg-success/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" className="text-success" size={22} />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-2">Our Integration</h4>
                      <p className="text-text-secondary leading-relaxed">{sol}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Features & Benefits Split Grid */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-success/5 border-t border-b border-border">
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Features Card */}
                <div className="bg-card border border-border rounded-3xl p-8 lg:p-12 shadow-sm">
                  <h3 className="text-2xl font-black mb-8 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                      <Icon name="Layers" className="text-primary" size={20} />
                    </div>
                    <span>Key Features Included</span>
                  </h3>
                  <div className="space-y-6">
                    {service.features.map((feat, idx) => {
                      const [title, desc] = feat.split(': ');
                      return (
                        <div key={idx} className="flex items-start space-x-3">
                          <Icon name="Zap" size={16} className="text-accent mt-1 flex-shrink-0 animate-pulse" />
                          <div>
                            <span className="font-bold text-foreground block">{title}</span>
                            <span className="text-text-secondary text-sm leading-relaxed">{desc}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Benefits Card */}
                <div className="bg-card border border-border rounded-3xl p-8 lg:p-12 shadow-sm flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-black mb-8 flex items-center space-x-3">
                      <div className="w-10 h-10 bg-success/20 rounded-xl flex items-center justify-center">
                        <Icon name="TrendingUp" className="text-success" size={20} />
                      </div>
                      <span>Direct Business Benefits</span>
                    </h3>
                    <div className="space-y-6 mb-8">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-success/15 rounded-full flex items-center justify-center flex-shrink-0 text-success text-xs font-bold">
                            {idx + 1}
                          </div>
                          <span className="text-text-secondary leading-relaxed font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-6 bg-muted rounded-2xl border border-border/80 text-center">
                    <p className="text-sm text-text-secondary font-medium mb-4">
                      Have specific features in mind? We customize everything down to the single line of code.
                    </p>
                    <Button variant="outline" size="sm" onClick={openConsultation}>
                      Discuss Your Feature List
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Suitable Industries Links */}
          <section className="py-20 bg-background">
            <div className="max-w-6xl mx-auto px-6 text-center">
              <h2 className="text-3xl font-black mb-4">Suitable Industries</h2>
              <p className="text-text-secondary max-w-2xl mx-auto mb-12">
                This service integrates smoothly with multiple sectors. Select an industry to learn more about how we specialize in it.
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                {matchedIndustries.map((ind) => (
                  <Link
                    key={ind.id}
                    to={`/industries/${ind.id}`}
                    className="inline-flex items-center space-x-2 bg-card border border-border px-5 py-3 rounded-xl hover:border-primary/50 hover:shadow-brand transition-all duration-300 font-bold text-text-secondary hover:text-primary"
                  >
                    <Icon name={ind.icon} size={16} />
                    <span>{ind.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Friendly CTA */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-success/5 text-center border-t border-border">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-3xl md:text-5xl font-black mb-6">Let's Solve Your Business Problems</h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-8">
                No complex contracts, no sales pressure. Tell us what operational challenges you face, and we will sketch out a potential solution for free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  className="bg-gradient-to-r from-primary to-accent text-white"
                  onClick={openConsultation}
                >
                  Schedule A Quick Consultation
                </Button>
                <Link to="/contact-engagement-hub" className="text-sm font-semibold text-text-secondary hover:text-primary transition-colors">
                  Or view other contact methods
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ServiceDetailPage;
